import { CaseActivity } from "@/components/cases/case-activities";
import { CaseHistory } from "@/components/cases/case-history";
import { CaseParty } from "@/components/cases/case-party";
import CaseStages from "@/components/cases/case-stages";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import FloatingAction from "@/components/core/actions/floating-action";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataGrid from "@/components/core/data-grid";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { CaseCollaborators } from "@/features/new-case-ui/case-collaborators";
import { CaseEvidence } from "@/features/new-case-ui/case-evidence";
import { CaseBlock, CaseBlockBody, CaseBlockHead, CaseLayout } from "@/features/new-case-ui/case-layout";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { Link, createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_backoffice/new-cases")({
	component: Page,
});

type CourtCase = {
	case_category: string;
	status: string;
	stage: string;
	case_type: string;
	date_filed: string;
	date_raffled: string;
	date_received: string;
	received_by: string;
};

type FileRecord = {
	case_number: string;
	file_type: string;
	file_size: string;
	date_created: string;
};

const caseOverviewColumns: ColumnDef<CourtCase>[] = [
	{
		id: "case_category",
		label: "Case Category",
		cell: ({ data }) => <span>{data.case_category}</span>,
	},
	{
		id: "status",
		label: "Status",
		cell: ({ data }) => <span>{data.status}</span>,
	},
	{
		id: "stage",
		label: "Stage",
		cell: ({ data }) => <span>{data.stage}</span>,
	},
	{
		id: "case_type",
		label: "Case Type",
		cell: ({ data }) => <span>{data.case_type}</span>,
	},
	{
		id: "date_filed",
		label: "Date Filed",
		cell: ({ data }) => <span>{data.date_filed}</span>,
	},
	{
		id: "date_raffled",
		label: "Date Raffled",
		cell: ({ data }) => <span>{data.date_raffled}</span>,
	},
	{
		id: "date_received",
		label: "Date Received",
		cell: ({ data }) => <span>{data.date_received}</span>,
	},
	{
		id: "received_by",
		label: "Received By",
		cell: ({ data }) => <span>{data.received_by}</span>,
	},
];

const caseOverview: CourtCase = {
	case_category: "Civil",
	status: "Pending",
	stage: "Pre-trial",
	case_type: "Breach of Contract",
	date_filed: "2023-05-12",
	date_raffled: "2023-05-20",
	date_received: "2023-05-22",
	received_by: "Clerk A",
};

const caseDocumentsColumns: ColumnDef<FileRecord>[] = [
	{
		id: "case_number",
		label: "File Name",
		cellClassName: "",
		cell: ({ data }) => (
			<Link to="/" className="font-semibold text-blue-600 hover:text-blue-500">
				{data.case_number}
			</Link>
		),
	},
	{
		id: "file_type",
		label: "File Type",
		cellClassName: "",
		cell: ({ data }) => data.file_type,
	},
	{
		id: "file_size",
		label: "File Size",
		cellClassName: "",
		cell: ({ data }) => data.file_size,
	},
	{
		id: "date_created",
		label: "Date Created",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => data.date_created,
	},
];

const caseDocuments: FileRecord[] = [
	{
		case_number: "2024-CV-001",
		file_type: "PDF",
		file_size: "1.2 MB",
		date_created: "2024-02-15",
	},
	{
		case_number: "2024-CR-002",
		file_type: "Word Document",
		file_size: "512 KB",
		date_created: "2024-01-10",
	},
];

const plaintiffs = [
	{ id: 1, name: "Michael Thornton" },
	{ id: 2, name: "Emma Holloway" },
];

const plaintiffCounsels = [
	{ id: 1, name: "Ethan Caldwell" },
	{ id: 2, name: "Sophia Martinez" },
	{ id: 3, name: "Benjamin Harper" },
	{ id: 4, name: "Olivia Dawson" },
	{ id: 5, name: "Nathaniel Vaughn" },
	{ id: 6, name: "Isabella Reynolds" },
	{ id: 7, name: "Elijah Montgomery" },
	{ id: 8, name: "Charlotte Hayes" },
	{ id: 9, name: "Alexander Foster" },
	{ id: 10, name: "Amelia Brooks" },
	{ id: 11, name: "Lucas Bennett" },
	{ id: 12, name: "Victoria Ellis" },
	{ id: 13, name: "Daniel Whitaker" },
	{ id: 14, name: "Emily Sutton" },
	{ id: 15, name: "Samuel Harrington" },
	{ id: 16, name: "Ava Lambert" },
	{ id: 17, name: "Henry Mitchell" },
	{ id: 18, name: "Madison Tate" },
	{ id: 19, name: "William Carrington" },
	{ id: 20, name: "Grace Sinclair" },
];

const defendants = [
	{ id: 1, name: "Robert Kensington" },
	{ id: 2, name: "Laura Whitmore" },
	{ id: 3, name: "Daniel Prescott" },
	{ id: 4, name: "Olivia Remington" },
];

const defendantCounsels = [
	{ id: 1, name: "Jonathan Mercer" },
	{ id: 2, name: "Catherine Holloway" },
	{ id: 3, name: "David Kensington" },
	{ id: 4, name: "Margaret Winslow" },
	{ id: 5, name: "Gregory Thornton" },
	{ id: 6, name: "Elizabeth Calloway" },
	{ id: 7, name: "Samuel Prescott" },
	{ id: 8, name: "Rebecca Langston" },
	{ id: 9, name: "Thomas Whitmore" },
	{ id: 10, name: "Eleanor Fairfax" },
	{ id: 11, name: "Christopher Alden" },
	{ id: 12, name: "Victoria Remington" },
];

function Page() {
	return (
		<Content pageName="Cases">
			{/*<Header title="Cases" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center w-full max-w-full">
					<div className="inline-flex flex-col w-auto">
						<div className="inline-flex flex-row items-center gap-2">
							<span className="font-bold text-xl text-foreground-dark">Case No. 1</span>
							<Badge className="uppercase" size="xs" color="green-light">
								Ongoing Prelims
							</Badge>
						</div>
						<span className="text-base text-foreground/70">Some case description goes here</span>
					</div>
					<div className="inline-flex flex-row w-auto ml-auto gap-2">
						<DropdownMenu modal={false}>
							<DropdownMenuTrigger asChild>
								<Button>
									Actions
									<IconChevronDown size={16} stroke={2} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuGroup>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Some Action</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Another Action</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">More Actions</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onSelect={() => alert(1)} dangerZone>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Danger Zone</span>
										</div>
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					<Tabs defaultValue="case-details">
						<TabsList>
							<TabsTrigger value="case-details">Case Details</TabsTrigger>
							<TabsTrigger value="case-flow">Case Flow</TabsTrigger>
							<TabsTrigger value="case-history">Case History</TabsTrigger>
							<TabsTrigger value="case-collaborators">Case Collaborators</TabsTrigger>
						</TabsList>
						<TabsContent value="case-details">
							<CaseLayout>
								<CaseBlock>
									<CaseBlockHead>
										<div className="flex flex-col w-auto">
											<span className="text-base font-bold text-foreground-dark">Case Parties</span>
										</div>
									</CaseBlockHead>
									<CaseBlockBody className="border border-border/50">
										<div className="flex flex-col w-full">
											<CaseParty type="plaintiff" partyData={plaintiffs} counselData={plaintiffCounsels} />
											<CaseParty type="defendant" partyData={defendants} counselData={defendantCounsels} />
										</div>
									</CaseBlockBody>
								</CaseBlock>
								<CaseBlock>
									<CaseBlockHead>
										<div className="flex flex-col w-auto">
											<span className="text-base font-bold text-foreground-dark">Case Overview</span>
										</div>
									</CaseBlockHead>
									<CaseBlockBody className="border border-border/50">
										<DataGrid data={caseOverview} columns={caseOverviewColumns} />
									</CaseBlockBody>
								</CaseBlock>
								<CaseBlock>
									<CaseBlockHead>
										<div className="flex flex-col w-auto">
											<span className="text-base font-bold text-foreground-dark">Complaint and Annexes</span>
										</div>
									</CaseBlockHead>
									<CaseBlockBody className="">
										<DataTable
											className="border-collapse"
											data={caseDocuments}
											columns={caseDocumentsColumns}
											stickyHead={false}
											stickyCol={false}
											showBorder={true}
											compact={false}
											showToolbar={false}
										/>
									</CaseBlockBody>
								</CaseBlock>
								<CaseBlock>
									<CaseBlockHead>
										<div className="flex flex-col w-auto">
											<span className="text-base font-bold text-foreground-dark">Pieces of Evidence</span>
										</div>
									</CaseBlockHead>
									<CaseBlockBody className="border border-border/50">
										<div className="flex flex-col w-full">
											<CaseEvidence type="plaintiff" />
											<CaseEvidence type="defendant" />
										</div>
									</CaseBlockBody>
								</CaseBlock>
							</CaseLayout>
						</TabsContent>
						<TabsContent value="case-flow">
							<CaseStages
								stages={[
									{
										label: "Preliminary Proceedings",
										value: "Preliminary Proceedings",
									},
									{
										label: "Pre-Trial Conference",
										value: "Pre-Trial Conference",
									},
									{
										label: "Trial Proper",
										value: "Trial Proper",
									},
									{ label: "Judgment", value: "Judgement" },
									{
										label: "Post Judgment",
										value: "Post Judgement",
									},
								]}
								value="Pre-Trial Conference"
								activities={(stage) => {
									return (
										<div className="flex flex-col w-full gap-4">
											<div className="flex flex-row justify-between items-center w-full gap-4">
												<span className="font-bold text-foregtound-dark text-base">{stage}</span>
												<div className="inline-flex flex-row items-center gap-1">
													<Button size="sm">
														<IconPlus size={14} stroke={2} />
														<span className="">Add Schedule</span>
													</Button>
													<Button size="sm">
														<IconPlus size={14} stroke={2} />
														<span className="">Add Activity</span>
													</Button>
												</div>
											</div>
											<CaseActivity />
										</div>
									);
								}}
							/>
						</TabsContent>
						<TabsContent value="case-history">
							<CaseHistory />
						</TabsContent>
						<TabsContent value="case-collaborators">
							<CaseCollaborators />
						</TabsContent>
					</Tabs>
				</div>

				<FloatingAction
					title="Case Actions"
					description="Please select appropriate action:"
					/*actions={[
            {
              label: "Receive Case",
              variant: "default",
              onClick: () => alert(1),
            },
          ]}*/
				>
					<Button size="sm">AAAA</Button>
				</FloatingAction>
			</div>
		</Content>
	);
}
